import type { CSSProperties, ReactNode } from "react";

/**
 * Lightweight UI primitives used by the modern shell (landing, auth,
 * lobby, dashboards, debrief). The radar canvas + Garmin/Raymarine MFD
 * chromes deliberately don't use these — those keep their device-
 * authentic look.
 */

export interface ButtonProps {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  size?: "sm" | "md" | "lg";
  disabled?: boolean;
  type?: "button" | "submit";
  ariaLabel?: string;
  style?: CSSProperties;
  fullWidth?: boolean;
}

export function Button(props: ButtonProps): JSX.Element {
  const variant = props.variant ?? "primary";
  const size = props.size ?? "md";
  const padding =
    size === "sm" ? "6px 12px" : size === "lg" ? "12px 22px" : "9px 16px";
  const fontSize = size === "sm" ? 12 : size === "lg" ? 15 : 13;

  let bg: string;
  let color: string;
  let border: string;
  switch (variant) {
    case "primary":
      bg = "var(--accent)";
      color = "var(--accent-fg)";
      border = "var(--accent)";
      break;
    case "secondary":
      bg = "transparent";
      color = "var(--text)";
      border = "var(--border-strong)";
      break;
    case "ghost":
      bg = "transparent";
      color = "var(--text-muted)";
      border = "transparent";
      break;
    case "danger":
      bg = "transparent";
      color = "var(--danger)";
      border = "var(--danger)";
      break;
  }

  return (
    <button
      type={props.type ?? "button"}
      onClick={props.onClick}
      disabled={props.disabled}
      aria-label={props.ariaLabel}
      style={{
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        gap: 6,
        background: bg,
        color,
        border: `1px solid ${border}`,
        borderRadius: "var(--radius-sm)",
        padding,
        fontSize,
        fontWeight: variant === "primary" ? 600 : 500,
        cursor: props.disabled ? "not-allowed" : "pointer",
        opacity: props.disabled ? 0.5 : 1,
        transition: "background 120ms ease, transform 120ms ease",
        width: props.fullWidth === true ? "100%" : undefined,
        ...props.style,
      }}
    >
      {props.children}
    </button>
  );
}

export function Card(props: {
  children: ReactNode;
  style?: CSSProperties;
  padding?: number;
}): JSX.Element {
  return (
    <div
      style={{
        background: "var(--bg-card)",
        border: "1px solid var(--border)",
        borderRadius: "var(--radius-md)",
        padding: props.padding ?? 20,
        boxShadow: "var(--shadow-sm)",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
}

export function Heading(props: {
  level?: 1 | 2 | 3;
  children: ReactNode;
  style?: CSSProperties;
  id?: string;
}): JSX.Element {
  const level = props.level ?? 2;
  const Tag = (`h${level}` as unknown) as "h1";
  const fontSize = level === 1 ? 32 : level === 2 ? 18 : 14;
  return (
    <Tag
      id={props.id}
      style={{
        fontSize,
        margin: 0,
        color: "var(--text)",
        ...props.style,
      }}
    >
      {props.children}
    </Tag>
  );
}

export function Label(props: {
  children: ReactNode;
  htmlFor?: string;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <label
      htmlFor={props.htmlFor}
      style={{
        display: "block",
        fontSize: 12,
        fontWeight: 500,
        color: "var(--text-muted)",
        marginBottom: 6,
        ...props.style,
      }}
    >
      {props.children}
    </label>
  );
}

export function Input(props: {
  value: string;
  onChange: (v: string) => void;
  type?: "text" | "email" | "password";
  placeholder?: string;
  ariaLabel?: string;
  inputMode?: "text" | "decimal" | "numeric" | "email";
  maxLength?: number;
  disabled?: boolean;
  style?: CSSProperties;
  autoComplete?: string;
}): JSX.Element {
  return (
    <input
      type={props.type ?? "text"}
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
      placeholder={props.placeholder}
      aria-label={props.ariaLabel}
      inputMode={props.inputMode}
      maxLength={props.maxLength}
      disabled={props.disabled}
      autoComplete={props.autoComplete}
      style={{
        background: "var(--bg-elev)",
        color: "var(--text)",
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-sm)",
        padding: "9px 12px",
        width: "100%",
        outline: "none",
        transition: "border-color 120ms ease",
        ...props.style,
      }}
    />
  );
}

export function Select<T extends string>(props: {
  value: T;
  onChange: (v: T) => void;
  options: ReadonlyArray<{ value: T; label: string }>;
  ariaLabel?: string;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <select
      value={props.value}
      onChange={(e) => props.onChange(e.target.value as T)}
      aria-label={props.ariaLabel}
      style={{
        background: "var(--bg-elev)",
        color: "var(--text)",
        border: "1px solid var(--border-strong)",
        borderRadius: "var(--radius-sm)",
        padding: "9px 12px",
        width: "100%",
        cursor: "pointer",
        ...props.style,
      }}
    >
      {props.options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function Stack(props: {
  children: ReactNode;
  gap?: number;
  direction?: "row" | "column";
  align?: "start" | "center" | "end" | "stretch";
  justify?: "start" | "center" | "end" | "between";
  wrap?: boolean;
  style?: CSSProperties;
}): JSX.Element {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: props.direction ?? "column",
        gap: props.gap ?? 12,
        alignItems:
          props.align === "center"
            ? "center"
            : props.align === "end"
              ? "flex-end"
              : props.align === "stretch"
                ? "stretch"
                : "flex-start",
        justifyContent:
          props.justify === "center"
            ? "center"
            : props.justify === "end"
              ? "flex-end"
              : props.justify === "between"
                ? "space-between"
                : "flex-start",
        flexWrap: props.wrap === true ? "wrap" : "nowrap",
        ...props.style,
      }}
    >
      {props.children}
    </div>
  );
}

export function Pill(props: {
  children: ReactNode;
  tone?: "neutral" | "accent" | "success" | "danger";
}): JSX.Element {
  const tone = props.tone ?? "neutral";
  const palette = {
    neutral: { bg: "var(--bg-elev)", color: "var(--text-muted)" },
    accent: { bg: "rgba(56,189,248,0.15)", color: "var(--accent)" },
    success: { bg: "rgba(16,185,129,0.15)", color: "var(--success)" },
    danger: { bg: "rgba(239,68,68,0.15)", color: "var(--danger)" },
  }[tone];
  return (
    <span
      style={{
        display: "inline-block",
        padding: "2px 10px",
        borderRadius: 999,
        fontSize: 11,
        fontWeight: 500,
        background: palette.bg,
        color: palette.color,
        textTransform: "uppercase",
        letterSpacing: 0.5,
      }}
    >
      {props.children}
    </span>
  );
}
